import {
  PacingModel,
  Segment,
  TerrainType,
  DerivedAthleteParams,
  ClimateImpact,
} from '../types'

// ============================================================
// Terrain-Adjusted Pacing Model — DEFAULT lookup tables
// ============================================================

export const DEFAULT_TERRAIN_FACTOR: Record<TerrainType, number> = {
  Road: 1.0,
  SmoothTrail: 1.05,
  MixedTrail: 1.1,
  Rocky: 1.2,
  Technical: 1.3,
  MudSand: 1.25,
  OfftrailSteep: 1.45,
}

export const DEFAULT_UPHILL_SEC_PER_100M: Record<TerrainType, number> = {
  Road: 90,
  SmoothTrail: 105,
  MixedTrail: 120,
  Rocky: 140,
  Technical: 175,
  MudSand: 150,
  OfftrailSteep: 210,
}

export const DEFAULT_DOWNHILL_SEC_PER_100M: Record<TerrainType, number> = {
  Road: -10,
  SmoothTrail: -8,
  MixedTrail: 0,
  Rocky: 20,
  Technical: 45,
  MudSand: 30,
  OfftrailSteep: 65,
}

export const TERRAIN_KEYS: TerrainType[] = [
  'Road',
  'SmoothTrail',
  'MixedTrail',
  'Rocky',
  'Technical',
  'MudSand',
  'OfftrailSteep',
]

// ============================================================
// Internal guardrails
// ============================================================
const INTERNAL_MIN_PACE_SEC_PER_KM = 4.5 * 60
const INTERNAL_MAX_PACE_SEC_PER_KM = 12.5 * 60
const INTERNAL_MAX_DOWNHILL_BONUS_RATIO = 0.12

const NO_DOWNHILL_BONUS_TERRAINS: Set<TerrainType> = new Set([
  'Rocky',
  'Technical',
  'MudSand',
  'OfftrailSteep',
])

const WINDOW_SIZE_KM = 0.15

// ============================================================
// GAP Model — Internal guardrails
// ============================================================
const GAP_MIN_PACE_MIN_PER_KM = 4.0
const GAP_MAX_PACE_MIN_PER_KM = 15.0

function gapGradeCost(gradePct: number): number {
  if (gradePct >= 0) {
    return 1.0 + gradePct * 0.05
  }
  const absGrade = Math.abs(gradePct)
  // Gentle downhill bonus
  if (absGrade <= 5) {
    return Math.max(1.0 - absGrade * 0.016, 0.92)
  }
  // Steep downhill cost
  return 0.92 + (absGrade - 5) * 0.03
}

function gapFatigueMultiplier(
  elapsedMinutes: number,
  onset: number,
  exponent: number,
  maxMult: number,
): number {
  if (elapsedMinutes <= onset) return 0
  const hoursOver = (elapsedMinutes - onset) / 60
  const rampHours = 6
  const t = Math.min(hoursOver / rampHours, 1.0)
  const curve = Math.pow(t, exponent) // Use exponent from profile
  return Math.min(curve * maxMult, maxMult)
}

// ============================================================
// Model 2: Grade Adjusted Pace (GAP)
// ============================================================

const GAPModel: PacingModel = {
  id: 'gap-v1',
  name: 'Grade Adjusted Pace (GAP)',
  description:
    'Effort-based pacing for runnable trails. Uses your sustainable flat effort and applies grade adjustments + fatigue.',
  computeSegmentTime: (
    segment: Segment,
    athleteParams: DerivedAthleteParams,
    climateImpact: ClimateImpact,
    extraParams: Record<string, any> = {},
  ) => {
    // 1. Base Pace from Profile
    const flatPace = athleteParams.flatPaceMinPerKm
    const elapsedMinutes = Number(extraParams._elapsedMinutes) || 0

    // 2. Grade Cost
    const grade = segment.avgGrade
    const cost = gapGradeCost(grade)

    // 3. Base Time
    let segmentTime = segment.distance * flatPace * cost

    // 4. Fatigue (from Profile)
    const fatigue = gapFatigueMultiplier(
      elapsedMinutes,
      athleteParams.fatigueOnsetMinutes,
      athleteParams.fatigueCurveExponent,
      athleteParams.maxFatigueMultiplier,
    )
    segmentTime *= 1 + fatigue

    // 5. Climate Impact (Additive)
    segmentTime *= climateImpact.pacingMultiplier

    // 6. Clamp
    const effectivePace =
      segment.distance > 0 ? segmentTime / segment.distance : flatPace
    const clampedPace = Math.max(
      GAP_MIN_PACE_MIN_PER_KM,
      Math.min(GAP_MAX_PACE_MIN_PER_KM, effectivePace),
    )

    return segment.distance * clampedPace
  },
}

// ============================================================
// Model 3: Terrain-Adjusted Pacing
// ============================================================

const TerrainAdjustedModel: PacingModel = {
  id: 'terrain-v1',
  name: 'Terrain-Adjusted Pacing',
  description:
    'Conservative pacing for technical/ultra trails. Uses terrain types, hike detection, and detailed uphill/downhill rules.',
  computeSegmentTime: (
    segment: Segment,
    athleteParams: DerivedAthleteParams,
    climateImpact: ClimateImpact,
    extraParams: Record<string, any> = {},
  ) => {
    const paceFlatSecPerKm = athleteParams.flatPaceMinPerKm * 60
    const minPace = INTERNAL_MIN_PACE_SEC_PER_KM
    const maxPace = INTERNAL_MAX_PACE_SEC_PER_KM

    // Apply profile modifiers to defaults
    const terrainFactors = { ...DEFAULT_TERRAIN_FACTOR }
    // Apply technical penalty to technical terrains
    if (athleteParams.technicalPenaltyMultiplier !== 1.0) {
      terrainFactors.Rocky *= athleteParams.technicalPenaltyMultiplier
      terrainFactors.Technical *= athleteParams.technicalPenaltyMultiplier
      terrainFactors.OfftrailSteep *= athleteParams.technicalPenaltyMultiplier
    }

    const uphillSecPer100m = { ...DEFAULT_UPHILL_SEC_PER_100M }
    // Apply uphill cost multiplier
    for (const k of TERRAIN_KEYS) {
      uphillSecPer100m[k] *= athleteParams.uphillCostMultiplier
    }

    const downhillSecPer100m = { ...DEFAULT_DOWNHILL_SEC_PER_100M }
    // Apply downhill bonus multiplier (only to negative values which represent time savings)
    for (const k of TERRAIN_KEYS) {
      if (downhillSecPer100m[k] < 0) {
        downhillSecPer100m[k] *= athleteParams.downhillBonusMultiplier
      }
    }

    const terrain: TerrainType = segment.terrainType || 'SmoothTrail'
    const noDownhillBonus = NO_DOWNHILL_BONUS_TERRAINS.has(terrain)

    // Sub-segment windowed calculation
    const gpxPoints = extraParams._gpxPoints as
      | Array<{ ele: number; distFromStart?: number }>
      | undefined

    let timeMinutes = 0
    if (gpxPoints && gpxPoints.length > 2) {
      const startIdx = segment.startPointIndex
      const endIdx = segment.endPointIndex
      if (startIdx >= 0 && endIdx > startIdx && endIdx < gpxPoints.length) {
        timeMinutes = computeWindowedTime(
          gpxPoints,
          startIdx,
          endIdx,
          terrain,
          paceFlatSecPerKm,
          minPace,
          maxPace,
          INTERNAL_MAX_DOWNHILL_BONUS_RATIO,
          terrainFactors,
          uphillSecPer100m,
          downhillSecPer100m,
          noDownhillBonus,
          athleteParams.hikeGradeThreshold,
        )
      } else {
        timeMinutes = computeWindowTime(
          segment.distance,
          segment.elevationGain,
          segment.elevationLoss,
          segment.avgGrade,
          terrain,
          paceFlatSecPerKm,
          minPace,
          maxPace,
          INTERNAL_MAX_DOWNHILL_BONUS_RATIO,
          terrainFactors,
          uphillSecPer100m,
          downhillSecPer100m,
          noDownhillBonus,
          athleteParams.hikeGradeThreshold,
        )
      }
    } else {
      timeMinutes = computeWindowTime(
        segment.distance,
        segment.elevationGain,
        segment.elevationLoss,
        segment.avgGrade,
        terrain,
        paceFlatSecPerKm,
        minPace,
        maxPace,
        INTERNAL_MAX_DOWNHILL_BONUS_RATIO,
        terrainFactors,
        uphillSecPer100m,
        downhillSecPer100m,
        noDownhillBonus,
        athleteParams.hikeGradeThreshold,
      )
    }

    // Apply Climate Impact
    return timeMinutes * climateImpact.pacingMultiplier
  },
}

function computeWindowTime(
  dist_km: number,
  dplus_m: number,
  dminus_m: number,
  avgGradePct: number,
  terrain: TerrainType,
  paceFlatSecPerKm: number,
  minPace: number,
  maxPace: number,
  maxDownhillBonusRatio: number,
  terrainFactors: Record<string, number>,
  uphillSecPer100m: Record<string, number>,
  downhillSecPer100m: Record<string, number>,
  noDownhillBonus: boolean,
  hikeThreshold: number,
): number {
  if (dist_km <= 0) return 0

  const pace_base = paceFlatSecPerKm * (terrainFactors[terrain] ?? 1.1)
  const t_flat = pace_base * dist_km
  const t_up = (dplus_m / 100) * (uphillSecPer100m[terrain] ?? 120)

  const downhill_rate = downhillSecPer100m[terrain] ?? 0
  let t_down_raw = (dminus_m / 100) * downhill_rate
  let t_down: number

  if (noDownhillBonus && t_down_raw < 0) {
    t_down = 0
  } else if (t_down_raw < 0) {
    const downhill_bonus_cap = -maxDownhillBonusRatio * t_flat
    t_down = Math.max(t_down_raw, downhill_bonus_cap)
  } else {
    t_down = t_down_raw
  }

  const t_raw = t_flat + t_up + t_down
  let pace_eff = dist_km > 0 ? t_raw / dist_km : maxPace

  const isHiking = Math.abs(avgGradePct) > hikeThreshold
  if (isHiking) {
    pace_eff = Math.min(maxPace, pace_eff)
  } else {
    pace_eff = Math.max(minPace, Math.min(maxPace, pace_eff))
  }

  return (pace_eff * dist_km) / 60
}

function computeWindowedTime(
  gpxPoints: Array<{ ele: number; distFromStart?: number }>,
  startIdx: number,
  endIdx: number,
  terrain: TerrainType,
  paceFlatSecPerKm: number,
  minPace: number,
  maxPace: number,
  maxDownhillBonusRatio: number,
  terrainFactors: Record<string, number>,
  uphillSecPer100m: Record<string, number>,
  downhillSecPer100m: Record<string, number>,
  noDownhillBonus: boolean,
  hikeThreshold: number,
): number {
  const segPoints = gpxPoints.slice(startIdx, endIdx + 1)
  if (segPoints.length < 2) return 0

  const segStartDist = segPoints[0].distFromStart || 0
  const segEndDist = segPoints[segPoints.length - 1].distFromStart || 0
  const totalDist = segEndDist - segStartDist
  if (totalDist <= 0) return 0

  const numWindows = Math.max(1, Math.round(totalDist / WINDOW_SIZE_KM))
  const windowSize = totalDist / numWindows

  let totalMinutes = 0
  let ptIdx = 0

  for (let w = 0; w < numWindows; w++) {
    const winStart = segStartDist + w * windowSize
    const winEnd = segStartDist + (w + 1) * windowSize

    let winGain = 0
    let winLoss = 0
    let prevEle = -1

    while (
      ptIdx < segPoints.length - 1 &&
      (segPoints[ptIdx].distFromStart || 0) < winStart
    ) {
      ptIdx++
    }
    if (ptIdx > 0) prevEle = segPoints[ptIdx].ele
    else prevEle = segPoints[0].ele

    for (let i = ptIdx; i < segPoints.length; i++) {
      const ptDist = segPoints[i].distFromStart || 0
      if (ptDist > winEnd + 0.001) break
      const eleDiff = segPoints[i].ele - prevEle
      if (eleDiff > 0) winGain += eleDiff
      else winLoss += Math.abs(eleDiff)
      prevEle = segPoints[i].ele
    }

    const winDistKm = windowSize
    const avgGrade =
      winDistKm > 0 ? ((winGain - winLoss) / (winDistKm * 1000)) * 100 : 0

    totalMinutes += computeWindowTime(
      winDistKm,
      winGain,
      winLoss,
      avgGrade,
      terrain,
      paceFlatSecPerKm,
      minPace,
      maxPace,
      maxDownhillBonusRatio,
      terrainFactors,
      uphillSecPer100m,
      downhillSecPer100m,
      noDownhillBonus,
      hikeThreshold,
    )
  }

  return totalMinutes
}

export const AVAILABLE_MODELS: PacingModel[] = [GAPModel, TerrainAdjustedModel]

export const getModelById = (id: string): PacingModel => {
  return AVAILABLE_MODELS.find((m) => m.id === id) || GAPModel
}

// Custom model: falls back to a base model but respects paceOverride on segments
export const CUSTOM_MODEL_ID = 'custom'

export function isCustomModel(id: string): boolean {
  return id === CUSTOM_MODEL_ID
}
